import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, Home } from 'lucide-react';

/**
 * RestrictedAccessPage - Shown when users without subscription access
 * try to access premium features directly via URL
 */
const RestrictedAccessPage: React.FC = () => {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        <div className="card p-8 md:p-12">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-warning-100 dark:bg-warning-900 flex items-center justify-center">
              <ShieldAlert className="w-10 h-10 text-warning-600 dark:text-warning-400" aria-hidden="true" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
            Subscription Required
          </h1>

          {/* Message */}
          <p className="text-lg text-text-secondary dark:text-text-dark-secondary mb-6">
            This feature requires an active subscription. You currently have access to the Dashboard and your shared Workspaces.
          </p>

          {/* Details */}
          <div className="bg-white dark:bg-background-dark-secondary rounded-lg p-6 mb-8 text-left">
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-3">
              What you can access:
            </h2>
            <ul className="space-y-2 text-text-secondary dark:text-text-dark-secondary">
              <li className="flex items-start">
                <span className="text-success-500 mr-2 mt-1">✓</span>
                <span><strong>Dashboard</strong> - View aggregated data from your workspaces</span>
              </li>
              <li className="flex items-start">
                <span className="text-success-500 mr-2 mt-1">✓</span>
                <span><strong>Workspaces</strong> - Collaborate in workspaces you&apos;ve been invited to</span>
              </li>
            </ul>

            <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mt-6 mb-3">
              To access additional features:
            </h2>
            <ul className="space-y-2 text-text-secondary dark:text-text-dark-secondary">
              <li className="flex items-start">
                <span className="text-text-tertiary dark:text-text-dark-tertiary mr-2 mt-1">•</span>
                <span>Create your own organization with a trial or paid subscription</span>
              </li>
              <li className="flex items-start">
                <span className="text-text-tertiary dark:text-text-dark-tertiary mr-2 mt-1">•</span>
                <span>Ask your workspace administrator to add you to their organization</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <Home className="w-5 h-5" aria-hidden="true" />
              Go to Dashboard
            </Link>
            <Link
              to="/workspaces"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary font-medium rounded-lg hover:bg-neutral-200 dark:hover:bg-background-dark-secondary transition-colors"
            >
              View My Workspaces
            </Link>
          </div>

          {/* Back Link */}
          <div className="mt-8 pt-6 border-t border-border-light dark:border-border-dark">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Go back
            </button>
          </div>
        </div>

        {/* Additional Help */}
        <p className="mt-6 text-sm text-text-tertiary dark:text-text-dark-tertiary">
          Need help? Contact your workspace administrator or{' '}
          <a href="mailto:support@capitolcommons.com" className="text-primary-600 dark:text-primary-400 hover:underline">
            reach out to support
          </a>
        </p>
      </div>
    </div>
  );
};

export default RestrictedAccessPage;





